import React, { useEffect, useState } from 'react';
import {
    Table, Input, Button, Select, Space, Tag,
    Popconfirm, message, Image, Modal, Form, InputNumber, Carousel,
    Upload,
    Row,
    Col,
    Descriptions
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';

const { Option } = Select;

const VehicleList = () => {
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sorter, setSorter] = useState({ field: 'created_at', order: 'descend' });
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('view');
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [form] = Form.useForm();

    const fetchData = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const res = await apiCall({
                method: 'GET',
                url: `vehicle?page=${page}&limit=${limit}&search=${search}&status=${statusFilter}&sort=${sorter.field}&order=${sorter.order === 'ascend' ? 'asc' : 'desc'}`,
                data: {
                    headers: { Authorization: 'Bearer ' + token },
                },
            });
            if (res.status === 'success') {
                setData(res.data.data);
                setMeta({
                    page: res.data.page,
                    limit: res.data.limit,
                    total: res.data.total_data,
                });
            } else {
                message.error(res.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error(error);
            message.error('An error occurred while fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(meta.page, meta.limit);
    }, [search, statusFilter, sorter]);

    const handleTableChange = (pagination, filters, sorterObj) => {
        setMeta((prev) => ({ ...prev, page: pagination.current, limit: pagination.pageSize }));
        setSorter({ field: sorterObj.field || 'created_at', order: sorterObj.order || 'descend' });
    };

    const openModal = (mode, record) => {
        setModalMode(mode);
        setSelectedVehicle(mode === 'create' ? null : record);
        setModalVisible(true);
    };

    useEffect(() => {
        if (modalVisible && selectedVehicle && modalMode === 'edit') {
            const imageFiles = selectedVehicle.vehicle_images?.map((img, index) => ({
                uid: `${index}`,
                name: `Image ${index + 1}`,
                status: 'done',
                url: img.Image,
            })) || [];

            form.setFieldsValue({
                license_plate: selectedVehicle.license_plate,
                brand: selectedVehicle.brand,
                model: selectedVehicle.model,
                year: selectedVehicle.year,
                color: selectedVehicle.color,
                vehicle_status: selectedVehicle.vehicle_status,
                last_location: selectedVehicle.last_location,
                stnk: selectedVehicle.stnk,
                type: selectedVehicle.type,
                chassis_number: selectedVehicle.chassis_number,
                engine_number: selectedVehicle.engine_number,
                images: imageFiles,
            });
        }

        if (modalVisible && modalMode === 'create') {
            form.resetFields();
        }
    }, [modalVisible, selectedVehicle, modalMode]);


    const handleDelete = async (id) => {
        try {
            const res = await apiCall({
                method: 'DELETE',
                url: `/vehicle/${id}`,
                data: {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            });
            if (res.status === 'success') {
                message.success('Berhasil menghapus kendaraan');
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message);
            }
        } catch (err) {
            message.error('Gagal menghapus kendaraan');
        }
    };

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            console.log(values)
            const url = modalMode === 'edit' ? `/vehicle/${selectedVehicle.vehicle_id}` : '/vehicle';
            const method = modalMode === 'edit' ? 'PUT' : 'POST';

            const formData = new FormData();
            Object.entries(values).forEach(([key, val]) => {
                if (key !== 'images') formData.append(key, val);
            });

            if (values.images) {
                values.images.forEach((file) => {
                    formData.append('images', file.originFileObj);
                });
            }

            const res = await apiCall({
                method,
                url,
                data: {
                    data: formData,
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'multipart/form-data',
                    },
                },
            });

            if (res.status === 'success') {
                message.success('Berhasil disimpan');
                setModalVisible(false);
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message);
            }
        } catch (err) {
            message.error('Validasi gagal atau error simpan');
        }
    };


    const columns = [
        {
            title: 'No',
            render: (_, __, index) => (meta.page - 1) * meta.limit + index + 1,
        },
        {
            title: 'Image',
            dataIndex: 'vehicle_images',
            render: (images) => (
                <Space>
                    {images?.slice(0, 1).map((img) => (
                        <Image
                            key={img.vehicle_image_id}
                            src={img.Image}
                            width={60}
                            height={40}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                    )) || '-'}
                </Space>
            ),
        },
        {
            title: 'License Plate',
            dataIndex: 'license_plate',
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
        },
        {
            title: 'Model',
            dataIndex: 'model',
        },
        {
            title: 'Year',
            dataIndex: 'year',
        },
        {
            title: 'Tipe',
            dataIndex: 'type',
        },
        {
            title: 'Status',
            dataIndex: 'vehicle_status',
            render: (status) => <Tag color={status === 'Available' ? 'green' : 'orange'}>{status}</Tag>,
        },
        {
            title: 'Aksi',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => openModal('view', record)}>Detail</Button>
                    <Button size="small" onClick={() => openModal('edit', record)}>Edit</Button>
                    <Popconfirm
                        title="Yakin ingin menghapus kendaraan ini?"
                        onConfirm={() => handleDelete(record.vehicle_id)}
                    >
                        <Button danger size="small">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const renderModalContent = () => {
        if (modalMode === 'view' && selectedVehicle) {
            return (
                <Descriptions bordered column={1} size="small">
                    {[
                        { label: 'License Plate', value: selectedVehicle.license_plate },
                        { label: 'Brand', value: selectedVehicle.brand },
                        { label: 'Model', value: selectedVehicle.model },
                        { label: 'Year', value: selectedVehicle.year },
                        { label: 'Color', value: selectedVehicle.color },
                        { label: 'STNK', value: selectedVehicle.stnk },
                        { label: 'Type', value: selectedVehicle.type },
                        { label: 'Chassis Number', value: selectedVehicle.chassis_number },
                        { label: 'Engine Number', value: selectedVehicle.engine_number },
                        { label: 'Status', value: selectedVehicle.vehicle_status, isTag: true },
                        { label: 'Last Location', value: selectedVehicle.last_location },
                    ].map((item) =>
                        item.value !== undefined &&
                            item.value !== null &&
                            item.value !== 'undefined' &&
                            item.value !== '' ? (
                            <Descriptions.Item key={item.label} label={item.label}>
                                {item.isTag ? (
                                    <Tag color={item.value === 'Available' ? 'green' : 'orange'}>
                                        {item.value}
                                    </Tag>
                                ) : (
                                    item.value
                                )}
                            </Descriptions.Item>
                        ) : null
                    )}

                    {selectedVehicle.vehicle_images?.length > 0 && (
                        <Descriptions.Item label="Images">
                            <Row>
                                {selectedVehicle.vehicle_images.map((img) => (
                                    <div style={{ width: 85 }}>
                                        <Image
                                            key={img.vehicle_image_id}
                                            src={img.Image}
                                            width={80}
                                            height={80}
                                        />
                                    </div>
                                ))}
                            </Row>
                        </Descriptions.Item>
                    )}
                </Descriptions>
            );
        }

        // Jika mode edit/create
        return (
            <Form layout="vertical" form={form}>
                <Form.Item name="license_plate" label="License Plate" rules={[{ required: true }]}><Input /></Form.Item>
                <Form.Item name="brand" label="Brand" rules={[{ required: true }]}><Input /></Form.Item>
                <Form.Item name="model" label="Model" rules={[{ required: true }]}><Input /></Form.Item>
                <Form.Item name="year" label="Year" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="color" label="Color"><Input /></Form.Item>
                <Form.Item name="stnk" label="STNK"><Input /></Form.Item>
                <Form.Item name="type" label="Tipe"><Input /></Form.Item>
                <Form.Item name="chassis_number" label="Nomor Rangka"><Input /></Form.Item>
                <Form.Item name="engine_number" label="Nomor Mesin"><Input /></Form.Item>
                <Form.Item name="vehicle_status" label="Status" rules={[{ required: true }]}>
                    <Select>
                        <Option value="Available">Available</Option>
                        <Option value="Borrowed">Borrowed</Option>
                        <Option value="Damaged">Damaged</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="last_location" label="Last Location"><Input /></Form.Item>
                <Form.Item name="images" label="Upload Images" valuePropName="fileList" getValueFromEvent={e => Array.isArray(e) ? e : e?.fileList}>
                    <Upload listType="picture" multiple beforeUpload={() => false} accept="image/*">
                        <Button>Upload</Button>
                    </Upload>
                </Form.Item>
            </Form>
        );
    };


    return (
        <div>
            <h2>Vehicle List</h2>
            <Row style={{ marginBottom: 10 }}>
                <Col span={6}>
                    <Input
                        placeholder="Search"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                    />
                </Col>
                <Col span={6}>
                    <Select
                        placeholder="Filter status"
                        allowClear
                        style={{ width: 160, marginLeft: 10 }}
                        onChange={setStatusFilter}
                    >
                        <Option value="Available">Available</Option>
                        <Option value="Borrowed">Borrowed</Option>
                        <Option value="Damaged">Damaged</Option>
                    </Select>
                </Col>
                <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end' }} >
                    <Button type="primary" onClick={() => openModal('create', null)}>Add Vehicle</Button>
                </Col>
            </Row>
            <Table
                columns={columns}
                dataSource={data}
                rowKey="vehicle_id"
                loading={loading}
                pagination={{
                    current: meta.page,
                    pageSize: meta.limit,
                    total: meta.total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setMeta((prev) => ({ ...prev, page, limit: pageSize }));
                        fetchData(page, pageSize);
                    }
                }}
                onChange={handleTableChange}
            />
            <Modal
                title={
                    modalMode === 'edit' ? 'Edit Vehicle' :
                        modalMode === 'create' ? 'Add Vehicle' : 'Vehicle Detail'
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={modalMode === 'view' ? () => setModalVisible(false) : handleModalSubmit}
                okText={modalMode === 'view' ? 'Close' : 'Save'}
                cancelButtonProps={modalMode === 'view' ? { style: { display: 'none' } } : {}}
                width={700}
                forceRender
            >
                {renderModalContent()}
            </Modal>
        </div>
    );
};

export default VehicleList;
